import type { FC } from 'react';
import {
  CloudRain,
  Microscope,
  ScrollText,
  ShoppingBasket,
  Sprout,
  SprayCan,
  UserRoundCheck,
  Wheat,
} from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  spent: {
    id: 'seasonTimeline.spent',
    defaultMessage: 'Spent this season',
    description: 'Label for the total the farmer has spent at the shop during this crop cycle.',
  },
  history: {
    id: 'seasonTimeline.history',
    defaultMessage: 'What has happened',
    description: 'Heading above the list of recorded events of the running crop season.',
  },
  improved: {
    id: 'seasonTimeline.outcome.improved',
    defaultMessage: 'Improved',
    description: 'Outcome tag: the problem got better after treatment.',
  },
  unchanged: {
    id: 'seasonTimeline.outcome.unchanged',
    defaultMessage: 'No change',
    description: 'Outcome tag: the problem stayed the same after treatment.',
  },
  worse: {
    id: 'seasonTimeline.outcome.worse',
    defaultMessage: 'Got worse',
    description: 'Outcome tag: the problem worsened after treatment.',
  },
  pending: {
    id: 'seasonTimeline.outcome.pending',
    defaultMessage: 'Not checked yet',
    description: 'Outcome tag: nobody has checked the result of this treatment yet.',
  },
});

interface SeasonEvent {
  date: string;
  kind: string;
  title: string;
  detail?: string;
  outcome?: string;
}

function readEvents(value: unknown): SeasonEvent[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const title = str(record.title);
    return title
      ? [
          {
            date: str(record.date),
            kind: str(record.kind),
            title,
            detail: optStr(record.detail),
            outcome: optStr(record.outcome),
          },
        ]
      : [];
  });
}

const EVENT_ICONS: Record<string, FC<{ className?: string; 'aria-hidden'?: boolean }>> = {
  sowing: Sprout,
  diagnosis: Microscope,
  spray: SprayCan,
  order: ShoppingBasket,
  visit: UserRoundCheck,
  weather: CloudRain,
  harvest: Wheat,
  note: ScrollText,
};

const OUTCOME_STYLES: Record<string, string> = {
  improved: 'border-success/60 text-success-foreground bg-success/10',
  unchanged: 'border-border text-muted-foreground bg-muted',
  worse: 'border-destructive/50 text-destructive bg-destructive/10',
  pending: 'border-border text-muted-foreground bg-transparent',
};

export const SeasonTimeline: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();

  const crop = str(node.props.crop);
  const plotLabel = optStr(node.props.plotLabel);
  const stage = optStr(node.props.stage);
  const startedOn = optStr(node.props.startedOn);
  const spentTotal = optStr(node.props.spentTotal);
  const events = readEvents(node.props.events);
  const nothingRecordedNote = optStr(node.props.nothingRecordedNote);

  const outcomeLabel = (outcome: string): string =>
    outcome === 'improved'
      ? intl.formatMessage(messages.improved)
      : outcome === 'unchanged'
        ? intl.formatMessage(messages.unchanged)
        : outcome === 'worse'
          ? intl.formatMessage(messages.worse)
          : intl.formatMessage(messages.pending);

  return (
    <section className="w-full rounded-lg border border-border bg-card p-5">
      <header className="border-b border-border pb-4">
        <h2
          className="text-balance text-2xl font-bold leading-tight tracking-tight text-foreground"
          translate="no"
        >
          {crop}
        </h2>
        {plotLabel || startedOn ? (
          <p className="mt-1 text-sm text-muted-foreground">
            {[plotLabel, startedOn].filter(Boolean).join(' · ')}
          </p>
        ) : null}

        {stage || spentTotal ? (
          <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
            {stage ? (
              <p className="min-w-0 max-w-[28rem] text-base font-semibold leading-snug text-foreground">
                {stage}
              </p>
            ) : null}
            {spentTotal ? (
              <p className="shrink-0">
                <span className="block text-xs font-semibold uppercase tracking-caps text-muted-foreground">
                  {intl.formatMessage(messages.spent)}
                </span>
                <span className="block text-xl font-bold tabular-nums text-foreground">
                  {spentTotal}
                </span>
              </p>
            ) : null}
          </div>
        ) : null}
      </header>

      {events.length > 0 ? (
        <>
          <h3 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.history)}
          </h3>
          <ol className="space-y-0">
            {events.map((event, index) => {
              const Icon = EVENT_ICONS[event.kind] ?? ScrollText;
              const isLast = index === events.length - 1;
              return (
                <li key={`${event.date}-${event.title}`} className="flex gap-3">
                  {/* The rail: mark plus the line that ties the season together. */}
                  <div className="flex flex-none flex-col items-center">
                    <span className="grid size-8 place-items-center rounded-full border border-border bg-background">
                      <Icon className="size-4 text-primary" aria-hidden />
                    </span>
                    {!isLast ? <span className="w-px flex-1 bg-border" aria-hidden /> : null}
                  </div>

                  <div className={isLast ? 'min-w-0 pb-0' : 'min-w-0 pb-5'}>
                    <p className="text-xs font-semibold uppercase tracking-caps text-muted-foreground">
                      {event.date}
                    </p>
                    <p className="mt-0.5 text-base font-semibold leading-snug text-foreground">
                      {event.title}
                    </p>
                    {event.detail ? (
                      <p className="mt-0.5 text-sm leading-snug text-muted-foreground">
                        {event.detail}
                      </p>
                    ) : null}
                    {event.outcome ? (
                      <span
                        className={`mt-1.5 inline-block rounded-sm border px-2 py-0.5 text-xs font-semibold ${
                          OUTCOME_STYLES[event.outcome] ?? OUTCOME_STYLES.pending
                        }`}
                      >
                        {outcomeLabel(event.outcome)}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      ) : (
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {nothingRecordedNote}
        </p>
      )}
    </section>
  );
};
